import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn, formatCurrency } from '@/utils';
import type { PremiumBreakdown as PremiumBreakdownType } from '@/types/integrations';

interface PremiumBreakdownProps {
  breakdown: PremiumBreakdownType;
  taxAmount: number;
  grossPremium: number;
}

function formatINR(amount: number) {
  return formatCurrency(amount);
}

interface BreakdownRow {
  label: string;
  value: number;
  isNegative?: boolean;
  isBold?: boolean;
  className?: string;
}

export function PremiumBreakdown({ breakdown, taxAmount, grossPremium }: PremiumBreakdownProps) {
  const rows: BreakdownRow[] = [
    { label: 'OD Premium', value: breakdown.od_premium ?? 0 },
    { label: 'TP Premium', value: breakdown.tp_premium ?? 0 },
    { label: 'PA Premium', value: breakdown.pa_premium ?? 0 },
    { label: 'Addon Total', value: breakdown.addon_total ?? 0 },
    { label: 'NCB Discount', value: breakdown.ncb_discount ?? 0, isNegative: true, className: 'text-green-600 dark:text-green-400' },
    { label: 'Net Premium', value: grossPremium - taxAmount, isBold: true },
    { label: 'Tax (GST)', value: taxAmount },
    { label: 'Total Premium', value: grossPremium, isBold: true, className: 'text-primary-700 dark:text-primary-300' },
  ];

  return (
    <Disclosure>
      {({ open }) => (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Premium Breakdown
            </span>
            <ChevronDownIcon
              className={cn('h-4 w-4 text-gray-500 transition-transform duration-200', open && 'rotate-180')}
            />
          </Disclosure.Button>
          <Disclosure.Panel>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={row.label} className="flex justify-between px-4 py-2">
                    <td className="text-gray-600 dark:text-gray-400">{row.label}</td>
                    <td
                      className={cn(
                        'tabular-nums',
                        row.isBold && 'font-semibold text-gray-900 dark:text-white',
                        row.className
                      )}
                    >
                      {row.isNegative && (breakdown.ncb_discount ?? 0) > 0 ? '- ' : ''}
                      {formatINR(row.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
